import { useRegisterDrawer } from '../../shell/ShellContext'
import { Drawer, useDrawerContent } from '../../ds'
import { useWorkspaceStore } from '../../stores/workspaceStore'

interface ConfigPanelProps {
  title: string
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
}

/**
 * Slide-out configuration panel — the design system's `Drawer` at `size="md"` (480px).
 *
 * The Drawer owns the scrim, Escape (one stack for every overlay, overlays.md), the focus trap,
 * the `--dockw` stage, and — since MAR-381 — how long its content stays on screen: it keeps the
 * last open title and children through the slide-out and drops them when it ends. This
 * component runs no timer of its own. Backdrop click, Escape or the close button closes it.
 */
const ConfigPanel = ({ title, isOpen, onClose, children }: ConfigPanelProps) => {
  // ⚠️ The fifth right-anchored drawer, and the one both halves of the dock work missed: it had
  // no registration, so `openDrawerCount` stayed 0 and the dock never stood down for it. The
  // count follows what is actually on screen — through the slide-out, until the Drawer's
  // `onExited` — not just `isOpen`.
  const { value: present, onExited } = useDrawerContent(isOpen || null, isOpen)
  useRegisterDrawer(!!present)
  // The workspace is the Drawer's `scope`: a close caused by switching workspace drops the held
  // title and children at once, so A's settings never slide out under B (MAR-381). Read the
  // same way the panel's sections read it.
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspace)?.id

  return (
    <Drawer open={isOpen} onClose={onClose} onExited={onExited} scope={workspaceId} title={title} size="md" modal={false} closeLabel="Close panel">
      <div data-testid="config-panel">{children}</div>
    </Drawer>
  )
}

export default ConfigPanel
